import { useLayoutEffect, useState } from "react";
import {
  maskOpsPresentationPhoneNumbers,
  maskOpsPresentationPhoneNumbersPreservingOffsets,
  projectOpsPresentationText,
  type OpsPresentationPrivacy
} from "@business-ops/domain";

export interface PrivateModeScope {
  workspaceId: string;
  memberId: string;
}

export interface PrivateModeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface PrivateTextProjection {
  truth: string;
  presentation: string;
}

export interface PrivateTextSpanProjection extends PrivateTextProjection {
  before: string;
  span: string;
  after: string;
}

const PRIVATE_MODE_STORAGE_PREFIX = "business-ops:private-mode:v1:";
const TEXT_NODE = 3;
const ELEMENT_NODE = 1;
const SHOW_ELEMENT_AND_TEXT = 5;
const PRIVATE_ATTRIBUTES = ["aria-label", "title", "alt"] as const;
const PRIVATE_TEXT_EXCLUSION = "script, style, noscript, template, input, textarea, [contenteditable='true'], [contenteditable='']";
const PRIVATE_PHONE_ACTION_SELECTOR = "[data-private-phone-action-blocked='true']";
const PRIVATE_PHONE_ACTION_NATIVE_CONTROL = "button, input, select, textarea, fieldset, optgroup, option";

export function privateModeStorageKey(scope: PrivateModeScope): string {
  return PRIVATE_MODE_STORAGE_PREFIX
    + encodeURIComponent(scope.workspaceId)
    + ":"
    + encodeURIComponent(scope.memberId);
}

export function readPrivateMode(storage: PrivateModeStorage | null, scope: PrivateModeScope): boolean {
  if (!storage) return true;
  try {
    // Fail closed: missing, stale, or malformed preferences keep masking on.
    return storage.getItem(privateModeStorageKey(scope)) !== "off";
  } catch {
    return true;
  }
}

export function writePrivateMode(storage: PrivateModeStorage | null, scope: PrivateModeScope, enabled: boolean): void {
  if (!storage) return;
  try {
    storage.setItem(privateModeStorageKey(scope), enabled ? "on" : "off");
  } catch {
    // Browser storage can be unavailable in hardened/private contexts. The
    // in-memory mode remains authoritative for this mounted signed-in session.
  }
}

export function browserPrivateModeStorage(): PrivateModeStorage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function maskPersonalPhoneNumbers(text: string): string {
  return maskOpsPresentationPhoneNumbers(text);
}

// WHY [BOL-PRIVATE-EVIDENCE-SPAN-001]: rendering before/highlight/after as
// separate text nodes let a phone candidate straddle all three and evade the
// node-level masker. Detect and mask the complete source while preserving its
// UTF-16 offsets, then split only the safe projection. Falsifier: recombining
// the rendered span exposes a middle phone digit or moves the canonical range.
export function maskPersonalPhoneNumbersPreservingOffsets(text: string): string {
  return maskOpsPresentationPhoneNumbersPreservingOffsets(text);
}

export function containsPersonalPhoneNumber(text: string): boolean {
  return maskPersonalPhoneNumbers(text) !== text;
}

function hasPersonalPhoneTarget(value: string): boolean {
  let decoded = value;
  try {
    decoded = decodeURIComponent(value);
  } catch {
    // Malformed escaping cannot promote a link into an allowed phone action.
  }
  const digits = decoded.replace(/\D/gu, "");
  return digits.length >= 8 && digits.length <= 15 && /^[1-9]/u.test(digits);
}

export function isPrivatePhoneActionHref(href: string): boolean {
  const value = href.trim();
  if (!value) return false;
  const directScheme = value.match(/^(?:tel|sms):([^?;,]+)/iu);
  if (directScheme) return hasPersonalPhoneTarget(directScheme[1] ?? "");

  try {
    const url = new URL(value, "https://private-mode.invalid");
    const host = url.hostname.toLocaleLowerCase("en");
    if (["wa.me", "www.wa.me"].includes(host)) {
      return hasPersonalPhoneTarget(url.pathname);
    }
    if (url.protocol === "whatsapp:"
      || ["api.whatsapp.com", "web.whatsapp.com", "www.whatsapp.com"].includes(host)) {
      return hasPersonalPhoneTarget(url.searchParams.get("phone") ?? url.searchParams.get("to") ?? "");
    }
  } catch {
    return false;
  }
  return false;
}

export function privateModePresentationPrivacy(enabled: boolean): OpsPresentationPrivacy {
  return enabled ? "MASK_CONTACT_DETAILS" : "SHOW_CONTACT_DETAILS";
}

function maskPrivatePresentationText(text: string, maskPhones: boolean): string {
  return projectOpsPresentationText(text, privateModePresentationPrivacy(maskPhones));
}

export function projectPrivateText(
  current: string,
  rememberedTruth?: string,
  maskPhones = true
): PrivateTextProjection {
  if (rememberedTruth !== undefined && current === maskPrivatePresentationText(rememberedTruth, maskPhones)) {
    return { truth: rememberedTruth, presentation: current };
  }
  return { truth: current, presentation: maskPrivatePresentationText(current, maskPhones) };
}

export function projectPrivateTextSpan(
  truth: string,
  start: number,
  end: number,
  maskPhones = true
): PrivateTextSpanProjection | null {
  if (!Number.isInteger(start) || !Number.isInteger(end) || start < 0 || end <= start || end > truth.length) {
    return null;
  }
  const presentation = maskPhones ? maskPersonalPhoneNumbersPreservingOffsets(truth) : truth;
  return {
    truth,
    presentation,
    before: presentation.slice(0, start),
    span: presentation.slice(start, end),
    after: presentation.slice(end)
  };
}

export function privateModePhoneMaskingFromRoot(
  root: Pick<HTMLElement, "getAttribute"> | null | undefined
): boolean {
  // Fail closed while the app root is unavailable or its mode is unsettled.
  return root?.getAttribute("data-private-mode") !== "off";
}

export function usePrivateModePhoneMasking(): boolean {
  const [maskPhones, setMaskPhones] = useState(() => privateModePhoneMaskingFromRoot(
    typeof document === "undefined" ? null : document.getElementById("root")
  ));
  useLayoutEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.getElementById("root");
    if (!root) return;
    const update = () => setMaskPhones(privateModePhoneMaskingFromRoot(root));
    update();
    const MutationObserverConstructor = root.ownerDocument.defaultView?.MutationObserver;
    if (!MutationObserverConstructor) return;
    const observer = new MutationObserverConstructor(update);
    observer.observe(root, { attributes: true, attributeFilter: ["data-private-mode"] });
    return () => observer.disconnect();
  }, []);
  return maskPhones;
}

export function restorePrivateText(current: string, rememberedTruth: string, maskPhones = true): string {
  return current === maskPrivatePresentationText(rememberedTruth, maskPhones) ? rememberedTruth : current;
}

export function installPrivateClipboardWriteMask(
  clipboard: Pick<Clipboard, "writeText"> | null | undefined,
  maskPhones = true
): () => void {
  if (!clipboard || typeof clipboard.writeText !== "function") return () => undefined;
  const ownDescriptor = Object.getOwnPropertyDescriptor(clipboard, "writeText");
  const original = clipboard.writeText;
  const wrapped = (text: string) => original.call(clipboard, maskPrivatePresentationText(text, maskPhones));
  try {
    Object.defineProperty(clipboard, "writeText", {
      configurable: true,
      writable: true,
      value: wrapped
    });
  } catch {
    return () => undefined;
  }
  return () => {
    try {
      if (clipboard.writeText !== wrapped) return;
      if (ownDescriptor) Object.defineProperty(clipboard, "writeText", ownDescriptor);
      else delete (clipboard as { writeText?: Clipboard["writeText"] }).writeText;
    } catch {
      // A browser may harden the Clipboard object after installation. Leaving
      // the privacy wrapper in place is safer than forcing an unsafe restore.
    }
  };
}

export function installPrivateModePresentation(root: HTMLElement, maskPhones = true): () => void {
  const ownerDocument = root.ownerDocument;
  const ownerWindow = ownerDocument.defaultView;
  const textTruth = new WeakMap<Text, string>();
  const attributeTruth = new WeakMap<Element, Map<string, string>>();
  const actionTruth = new WeakMap<Element, Map<string, { truth: string | null; presentation: string | null }>>();
  const priorModeAttribute = root.getAttribute("data-private-mode");

  const isExcluded = (element: Element | null) => Boolean(element?.closest(PRIVATE_TEXT_EXCLUSION));

  const projectTextNode = (node: Text) => {
    if (isExcluded(node.parentElement)) return;
    const current = node.data;
    const projected = projectPrivateText(current, textTruth.get(node), maskPhones);
    if (projected.presentation === projected.truth && !textTruth.has(node)) return;
    textTruth.set(node, projected.truth);
    if (current !== projected.presentation) node.data = projected.presentation;
  };

  const projectAttribute = (element: Element, name: string) => {
    if (isExcluded(element)) return;
    const current = element.getAttribute(name);
    const truths = attributeTruth.get(element);
    if (current === null) {
      truths?.delete(name);
      return;
    }
    const projected = projectPrivateText(current, truths?.get(name), maskPhones);
    if (projected.presentation === projected.truth && !truths?.has(name)) return;
    const nextTruths = truths ?? new Map<string, string>();
    nextTruths.set(name, projected.truth);
    attributeTruth.set(element, nextTruths);
    if (current !== projected.presentation) element.setAttribute(name, projected.presentation);
  };

  const actionHrefTruth = (element: Element): string | null => {
    const current = element.getAttribute("href");
    const projected = actionTruth.get(element)?.get("href");
    return projected && current === projected.presentation ? projected.truth : current;
  };

  const projectActionAttribute = (element: Element, name: string, presentation: string | null) => {
    const current = element.getAttribute(name);
    const attributes = actionTruth.get(element) ?? new Map<string, { truth: string | null; presentation: string | null }>();
    const remembered = attributes.get(name);
    const truth = remembered && current === remembered.presentation ? remembered.truth : current;
    attributes.set(name, { truth, presentation });
    actionTruth.set(element, attributes);
    if (presentation === null) {
      if (current !== null) element.removeAttribute(name);
    } else if (current !== presentation) {
      element.setAttribute(name, presentation);
    }
  };

  const restorePhoneAction = (element: Element) => {
    const attributes = actionTruth.get(element);
    if (!attributes) return;
    for (const [name, projected] of attributes) {
      if (element.getAttribute(name) !== projected.presentation) continue;
      if (projected.truth === null) element.removeAttribute(name);
      else element.setAttribute(name, projected.truth);
    }
    actionTruth.delete(element);
  };

  const projectPhoneAction = (element: Element) => {
    const href = actionHrefTruth(element);
    const marked = element.getAttribute("data-phone-action") === "true";
    if (!maskPhones || (!marked && !isPrivatePhoneActionHref(href ?? ""))) {
      restorePhoneAction(element);
      return;
    }
    if (href !== null || actionTruth.get(element)?.has("href")) {
      projectActionAttribute(element, "href", null);
    }
    if (element.matches(PRIVATE_PHONE_ACTION_NATIVE_CONTROL)) {
      projectActionAttribute(element, "disabled", "");
    }
    projectActionAttribute(element, "aria-disabled", "true");
    projectActionAttribute(element, "tabindex", "-1");
    projectActionAttribute(element, "data-private-phone-action-blocked", "true");
  };

  const projectNode = (node: Node) => {
    const projectCurrent = (current: Node) => {
      if (current.nodeType === TEXT_NODE) {
        projectTextNode(current as Text);
      } else if (current.nodeType === ELEMENT_NODE) {
        const element = current as Element;
        for (const name of PRIVATE_ATTRIBUTES) projectAttribute(element, name);
        projectPhoneAction(element);
      }
    };
    projectCurrent(node);
    const walker = ownerDocument.createTreeWalker(node, SHOW_ELEMENT_AND_TEXT);
    while (walker.nextNode()) projectCurrent(walker.currentNode);
  };

  const restoreNode = (node: Node) => {
    const restoreCurrent = (current: Node) => {
      if (current.nodeType === TEXT_NODE) {
        const text = current as Text;
        const truth = textTruth.get(text);
        if (truth !== undefined) text.data = restorePrivateText(text.data, truth, maskPhones);
      } else if (current.nodeType === ELEMENT_NODE) {
        const element = current as Element;
        const truths = attributeTruth.get(element);
        if (truths) {
          for (const [name, truth] of truths) {
            const currentValue = element.getAttribute(name);
            if (currentValue !== null) element.setAttribute(name, restorePrivateText(currentValue, truth, maskPhones));
          }
        }
        restorePhoneAction(element);
      }
    };
    restoreCurrent(node);
    const walker = ownerDocument.createTreeWalker(node, SHOW_ELEMENT_AND_TEXT);
    while (walker.nextNode()) restoreCurrent(walker.currentNode);
  };

  const handleMutations = (records: readonly MutationRecord[]) => {
    for (const record of records) {
      if (record.type === "characterData") projectTextNode(record.target as Text);
      else if (record.type === "attributes" && record.attributeName) {
        const element = record.target as Element;
        if ((PRIVATE_ATTRIBUTES as readonly string[]).includes(record.attributeName)) {
          projectAttribute(element, record.attributeName);
        }
        if (["href", "data-phone-action"].includes(record.attributeName)) projectPhoneAction(element);
      } else {
        for (const node of record.removedNodes) restoreNode(node);
        for (const node of record.addedNodes) projectNode(node);
      }
    }
  };

  // WHY [BOL-PRIVATE-PRESENTATION-001]: masking must cover every current and
  // newly streamed surface without rewriting canonical state or every product
  // component. Project rendered text/safe labels before paint and make only an
  // exact phone action inert while phone privacy is on; remember every changed
  // presentation attribute for reversal while canonical values, IDs, JIDs,
  // routes, commands, payloads, and persistence stay untouched. Falsifier: a
  // streamed number paints raw, a phone action activates while Private Mode is
  // on, a non-phone action is blocked, or turning it off fails to restore the
  // exact current href/control state. Identity aliasing is always active for
  // demos; only phone masking/action blocking follows the eye control.
  root.setAttribute("data-private-mode", maskPhones ? "on" : "off");
  projectNode(root);

  const MutationObserverConstructor = ownerWindow?.MutationObserver;
  const observer = MutationObserverConstructor
    ? new MutationObserverConstructor(handleMutations)
    : null;
  observer?.observe(root, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: [...PRIVATE_ATTRIBUTES, "href", "data-phone-action"]
  });

  const handlePhoneAction = (event: Event) => {
    if (!maskPhones) return;
    if (event.type === "keydown") {
      const key = (event as KeyboardEvent).key;
      if (key !== "Enter" && key !== " ") return;
    }
    const target = event.target as Node | null;
    const element = target?.nodeType === ELEMENT_NODE
      ? target as Element
      : (target as { parentElement?: Element | null } | null)?.parentElement ?? null;
    const action = element?.closest(PRIVATE_PHONE_ACTION_SELECTOR);
    if (!action || !root.contains(action)) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  };
  root.addEventListener("click", handlePhoneAction, true);
  root.addEventListener("auxclick", handlePhoneAction, true);
  root.addEventListener("keydown", handlePhoneAction, true);

  const handleCopy = (event: Event) => {
    const clipboardEvent = event as ClipboardEvent;
    if (!clipboardEvent.clipboardData) return;
    const active = ownerDocument.activeElement;
    const textControl = active?.tagName === "INPUT" || active?.tagName === "TEXTAREA"
      ? active as HTMLInputElement | HTMLTextAreaElement
      : null;
    const start = textControl?.selectionStart ?? 0;
    const end = textControl?.selectionEnd ?? start;
    const copied = textControl
      ? textControl.value.slice(start, end)
      : ownerWindow?.getSelection()?.toString() ?? "";
    if (!copied) return;
    clipboardEvent.clipboardData.setData("text/plain", maskPrivatePresentationText(copied, maskPhones));
    clipboardEvent.preventDefault();
  };
  root.addEventListener("copy", handleCopy, true);

  const restoreClipboard = installPrivateClipboardWriteMask(ownerWindow?.navigator.clipboard, maskPhones);

  return () => {
    if (observer) {
      handleMutations(observer.takeRecords());
      observer.disconnect();
    }
    root.removeEventListener("click", handlePhoneAction, true);
    root.removeEventListener("auxclick", handlePhoneAction, true);
    root.removeEventListener("keydown", handlePhoneAction, true);
    root.removeEventListener("copy", handleCopy, true);
    restoreClipboard();
    restoreNode(root);
    if (priorModeAttribute === null) root.removeAttribute("data-private-mode");
    else root.setAttribute("data-private-mode", priorModeAttribute);
  };
}

export function usePrivateModePresentation(enabled: boolean): void {
  useLayoutEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.getElementById("root");
    if (!root) return;
    return installPrivateModePresentation(root, enabled);
  }, [enabled]);
}
